<template>
  <v-container>
    <v-form ref="form" v-model="valid" lazy-validation>
      <!-- Event Name Display -->
      <label>
        <strong>Event Name: </strong>
        {{ selectedEvent.EventName ? selectedEvent.EventName : "N/A" }}
      </label>

      <!-- Stallholder Name Display -->
      <div class="mt-3">
        <label>
          <strong>StallHolder Name: </strong>
          {{
            selectedStallholder.StallholderName
              ? selectedStallholder.StallholderName
              : "N/A"
          }}
        </label>
      </div>

      <!-- Stall Name -->
      <v-text-field
        :value="formData.StallName"
        label="Stall Name"
        :rules="stallNameRules"
        required
        @input="updateField('StallName', $event)"
      />

      <!-- Return Home After Adding Products -->
      <v-row class="mb-2">
        <v-col cols="6">
          <label>Return Home After Adding Products</label>
          <v-combobox
            :value="formData.AddButtonReturnHome"
            :items="addButtonReturnHomeTypes"
            item-text="name"
            item-value="name"
            :return-object="false"
            required
            @input="updateField('AddButtonReturnHome', $event)"
          />
        </v-col>
      </v-row>

      <!-- Order Numbers -->
      <v-row class="mb-2">
        <v-col cols="6">
          <label>Order Numbers</label>
          <v-combobox
            :value="formData.OrderNumbers"
            :items="orderNumberTypes"
            item-text="name"
            item-value="name"
            :return-object="false"
            required
            @input="updateField('OrderNumbers', $event)"
          />
        </v-col>
        <v-col cols="5" offset-md="1" style="margin-top: 45px; font-size: 18px">
          <span>Inherit Stallholder Settings</span>
        </v-col>
      </v-row>

      <!-- ABN -->
      <inheritable-field
        :value="formData.Abn"
        :event-value="eventSettings.Abn"
        :inherit="inheritanceStates.isSelectABN"
        :event-setting-label="eventSettings.Abn"
        label="ABN"
        required
        @input="updateField('Abn', $event)"
        @inherit-change="handleInheritanceChange('Abn', 'AbnInherit', $event)"
      />

      <!-- Payment Profile -->
      <v-row class="mb-2">
        <v-col cols="6">
          <label>Payment Profile</label>
          <v-row class="ml-1">
            <v-checkbox
              v-model="isCashSelected"
              label="Cash"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPaymentProfile"
            />
            <v-checkbox
              v-model="isCardSelected"
              label="Card"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPaymentProfile"
            />
            <v-checkbox
              v-model="isVoucherSelected"
              label="Voucher"
              :disabled="inheritanceStates.isSelectPaymentProfile"
            />
          </v-row>
        </v-col>
        <v-col cols="5" offset-md="1">
          <v-checkbox
            :input-value="inheritanceStates.isSelectPaymentProfile"
            :label="eventSettings.PaymentProfile"
            class="mr-5"
            style="padding-top: 12px"
            @change="
              handleInheritanceChange(
                'PaymentProfile',
                'PaymentProfileInherit',
                $event
              )
            "
          />
        </v-col>
      </v-row>

      <!-- Printing Profile -->
      <label>Printing Profile</label>
      <v-row class="ml-1">
        <v-col cols="6">
          <v-row>
            <v-checkbox
              v-model="isPrintSelected"
              label="Print"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPrintingProfile"
            />
            <v-checkbox
              v-model="isPromptSelected"
              label="Prompt"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPrintingProfile"
            />
            <v-checkbox
              v-model="isBypassSelected"
              label="Bypass"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPrintingProfile"
            />
          </v-row>
          <!-- Hidden validation field for printing profile -->
          <v-text-field
            :value="formData.PrintingProfile"
            :rules="eventPrintRules"
            style="display: none"
            required
          />
        </v-col>
        <v-col cols="5" offset-md="1">
          <v-checkbox
            :input-value="inheritanceStates.isSelectPrintingProfile"
            :label="eventSettings.PrintingProfile"
            class="mr-5"
            style="margin-top: 4px; margin-left: -8px"
            @change="
              handleInheritanceChange(
                'PrintingProfile',
                'PrintingProfileInherit',
                $event
              )
            "
          />
        </v-col>
      </v-row>

      <!-- Drink Limit Profile -->
      <inheritable-field
        :value="standardDrinkLimitEnforceDisplayValue"
        :event-value="
          getEnforceTypeName(eventSettings.StandardDrinkLimitEnforce)
        "
        :inherit="inheritanceStates.isSelectStandardDrinkLimitEnforce"
        :event-setting-label="
          getEnforceTypeName(eventSettings.StandardDrinkLimitEnforce)
        "
        @input="standardDrinkLimitEnforceDisplayValue = $event"
        @inherit-change="
          handleInheritanceChange(
            'StandardDrinkLimitEnforce',
            'StandardDrinkLimitEnforceInherit',
            $event
          )
        "
      >
        <template #field="{ disabled, value, updateValue }">
          <v-combobox
            :value="value"
            :items="enforceTypes"
            item-text="name"
            item-value="name"
            label="Drink Limit Profile"
            :return-object="false"
            required
            :disabled="disabled"
            @input="updateValue"
          />
        </template>
      </inheritable-field>

      <!-- Maximum Drink Limit -->
      <inheritable-field
        :value="formData.StandardDrinkLimit"
        :event-value="eventSettings.StandardDrinkLimit"
        :inherit="inheritanceStates.isSelectStandardDrinkLimit"
        :event-setting-label="
          eventSettings.StandardDrinkLimit
            ? eventSettings.StandardDrinkLimit.toString()
            : ''
        "
        label="Maximum Drink Limit"
        type="number"
        min="0"
        required
        @input="updateField('StandardDrinkLimit', $event)"
        @inherit-change="
          handleInheritanceChange(
            'StandardDrinkLimit',
            'StandardDrinkLimitInherit',
            $event
          )
        "
      >
        <template #field="{ disabled, value, updateValue }">
          <v-row>
            <v-col cols="10">
              <v-text-field
                :value="value"
                label="Maximum Drink Limit"
                type="number"
                min="0"
                required
                :disabled="disabled"
                @input="updateValue"
              />
            </v-col>
            <v-col cols="2">
              <v-btn small dark style="margin-top: 25px">
                <v-icon left x-small> mdi-calendar-clock </v-icon>
                Schedule
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </inheritable-field>

      <!-- Card Surcharge Percentage -->
      <inheritable-field
        :value="formData.CardSurchargePercentage"
        :event-value="eventSettings.CardSurchargePercentage"
        :inherit="inheritanceStates.isSelectCardSurchargePercentage"
        :event-setting-label="
          eventSettings.CardSurchargePercentage
            ? eventSettings.CardSurchargePercentage.toString()
            : ''
        "
        label="Card Surcharge Percentage"
        type="number"
        min="0"
        required
        @input="updateField('CardSurchargePercentage', $event)"
        @inherit-change="
          handleInheritanceChange(
            'CardSurchargePercentage',
            'CardSurchargePercentageInherit',
            $event
          )
        "
      />

      <!-- Manager Pin -->
      <v-text-field
        :value="formData.ManagerPin"
        label="Manager Pin"
        type="number"
        min="0"
        required
        @input="updateField('ManagerPin', $event)"
      />
    </v-form>
  </v-container>
</template>

<script>
import InheritableField from "./InheritableField.vue";

export default {
  name: "StallForm",
  components: {
    InheritableField,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
    eventSettings: {
      type: Object,
      required: true,
    },
    inheritanceStates: {
      type: Object,
      required: true,
    },
    selectedEvent: {
      type: Object,
      required: true,
    },
    selectedStallholder: {
      type: Object,
      required: true,
    },
    printingList: {
      type: Array,
      required: true,
    },
    enforceTypes: {
      type: Array,
      required: true,
    },
    addButtonReturnHomeTypes: {
      type: Array,
      required: true,
    },
    orderNumberTypes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      valid: true,
      stallNameRules: [(v) => !!v || "Stall Name is required"],
      eventPrintRules: [(v) => !!v || "Printing Profile is required"],
    };
  },
  computed: {
    isCashSelected: {
      get() {
        return (this.formData.PaymentProfile || []).includes("Cash");
      },
      set(value) {
        this.updatePaymentProfile("Cash", value);
      },
    },
    isCardSelected: {
      get() {
        return (this.formData.PaymentProfile || []).includes("Card");
      },
      set(value) {
        this.updatePaymentProfile("Card", value);
      },
    },
    isVoucherSelected: {
      get() {
        return (this.formData.PaymentProfile || []).includes("Voucher");
      },
      set(value) {
        this.updatePaymentProfile("Voucher", value);
      },
    },
    isPrintSelected: {
      get() {
        return this.formData.PrintingProfile === "Print";
      },
      set(value) {
        if (value) {
          this.updateField("PrintingProfile", "Print");
        }
      },
    },
    isPromptSelected: {
      get() {
        return this.formData.PrintingProfile === "Prompt";
      },
      set(value) {
        if (value) {
          this.updateField("PrintingProfile", "Prompt");
        }
      },
    },
    isBypassSelected: {
      get() {
        return this.formData.PrintingProfile === "Bypass";
      },
      set(value) {
        if (value) {
          this.updateField("PrintingProfile", "Bypass");
        }
      },
    },
    standardDrinkLimitEnforceDisplayValue: {
      get() {
        let boolValue = this.formData.StandardDrinkLimitEnforce;

        if (typeof boolValue === "string") {
          if (boolValue === "true" || boolValue === "Enforce") {
            boolValue = true;
          } else if (boolValue === "false" || boolValue === "Prompt") {
            boolValue = false;
          }
        }

        const enforceType = this.enforceTypes.find(
          (type) => type.id === boolValue
        );
        return enforceType ? enforceType.name : "";
      },
      set(nameValue) {
        const enforceType = this.enforceTypes.find(
          (type) => type.name === nameValue
        );
        const boolValue = enforceType ? enforceType.id : false;
        this.updateField("StandardDrinkLimitEnforce", boolValue);
      },
    },
  },
  methods: {
    updateField(field, value) {
      this.$emit("update-field", field, value);
    },
    getEnforceTypeName(boolValue) {
      const enforceType = this.enforceTypes.find(
        (type) => type.id === boolValue
      );
      return enforceType ? enforceType.name : "";
    },
    handleInheritanceChange(fieldKey, inheritKey, useStallholderSetting) {
      this.$emit(
        "inheritance-change",
        fieldKey,
        inheritKey,
        useStallholderSetting
      );
    },
    updatePaymentProfile(value, checked) {
      let currentProfile = [...(this.formData.PaymentProfile || [])];
      if (checked) {
        if (!currentProfile.includes(value)) {
          currentProfile.push(value);
        }
      } else {
        currentProfile = currentProfile.filter((item) => item !== value);
      }
      this.updateField("PaymentProfile", currentProfile);
    },
    validate() {
      return this.$refs.form.validate();
    },
  },
};
</script>
